import { memo } from 'react';
import type { GalleryProfileCard, GalleryCardVariant } from '../../types/gallery';
import './GalleryCard.css';

// ==========================================
// TYPES
// ==========================================

interface GalleryCardProps {
  profile: GalleryProfileCard;
  variant: GalleryCardVariant;
  onClick?: () => void;
  onSwipeStart?: () => void;
  isSelected?: boolean;
  showActions?: boolean;
  className?: string;
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatDistance(km?: number): string {
  if (km === undefined) return '';
  if (km < 1) return '< 1 km';
  return `${Math.round(km)} km`;
}

function formatLastActive(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 5) return 'Online now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return '';
}

// ==========================================
// COMPONENT
// ==========================================

function GalleryCardComponent({
  profile,
  variant,
  onClick,
  onSwipeStart,
  isSelected = false,
  showActions = true,
  className = '',
}: GalleryCardProps) {
  const {
    displayName,
    age,
    primaryPhotoUrl,
    thumbnailUrl,
    isVerified,
    isOnline,
    distanceKm,
    locationCity,
    bio,
    interests,
  } = profile;

  const imageUrl = variant === 'icon' ? thumbnailUrl || primaryPhotoUrl : primaryPhotoUrl;
  const lastActiveText = formatLastActive(profile.lastActiveAt);

  // ==========================================
  // ICON VARIANT (64px circular)
  // ==========================================

  if (variant === 'icon') {
    return (
      <div
        className={`gallery-card gallery-card--icon ${isSelected ? 'selected' : ''} ${className}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      >
        <div className="gallery-card__avatar">
          {imageUrl ? (
            <img src={imageUrl} alt={displayName} loading="lazy" />
          ) : (
            <div className="gallery-card__avatar-placeholder">{getInitials(displayName)}</div>
          )}
          {isOnline && <span className="gallery-card__online-dot" />}
          {isVerified && <span className="gallery-card__verified-badge">✓</span>}
        </div>
        <span className="gallery-card__name">{displayName.split(' ')[0]}</span>
      </div>
    );
  }

  // ==========================================
  // COMPACT VARIANT (120px square)
  // ==========================================

  if (variant === 'compact') {
    return (
      <div
        className={`gallery-card gallery-card--compact ${isSelected ? 'selected' : ''} ${className}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      >
        <div className="gallery-card__image">
          {imageUrl ? (
            <img src={imageUrl} alt={displayName} loading="lazy" />
          ) : (
            <div className="gallery-card__image-placeholder">{getInitials(displayName)}</div>
          )}
          {isOnline && <span className="gallery-card__online-indicator">Online</span>}
          {isVerified && <span className="gallery-card__verified-icon">✓</span>}
        </div>
        <div className="gallery-card__info">
          <span className="gallery-card__name-age">
            {displayName.split(' ')[0]}, {age}
          </span>
          {distanceKm !== undefined && (
            <span className="gallery-card__distance">{formatDistance(distanceKm)}</span>
          )}
        </div>
        {showActions && (
          <button
            className="gallery-card__swipe-btn"
            onClick={(e) => {
              e.stopPropagation();
              onSwipeStart?.();
            }}
            title="Start swiping from this profile"
          >
            →
          </button>
        )}
      </div>
    );
  }

  // ==========================================
  // FULL VARIANT (detailed card)
  // ==========================================

  return (
    <div
      className={`gallery-card gallery-card--full ${isSelected ? 'selected' : ''} ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      <div className="gallery-card__photo">
        {imageUrl ? (
          <img src={imageUrl} alt={displayName} loading="lazy" />
        ) : (
          <div className="gallery-card__photo-placeholder">{getInitials(displayName)}</div>
        )}
        {isOnline && <span className="gallery-card__status">Online</span>}
        {!isOnline && lastActiveText && (
          <span className="gallery-card__status gallery-card__status--away">{lastActiveText}</span>
        )}
      </div>

      <div className="gallery-card__content">
        <div className="gallery-card__header">
          <h3 className="gallery-card__title">
            {displayName}, {age}
            {isVerified && <span className="gallery-card__verified">✓</span>}
          </h3>
          {locationCity && (
            <span className="gallery-card__location">
              📍 {locationCity}
              {distanceKm !== undefined && ` • ${formatDistance(distanceKm)}`}
            </span>
          )}
        </div>

        {bio && <p className="gallery-card__bio">{bio}</p>}

        {interests && interests.length > 0 && (
          <div className="gallery-card__interests">
            {interests.slice(0, 4).map((interest) => (
              <span key={interest} className="gallery-card__interest-tag">
                {interest}
              </span>
            ))}
            {interests.length > 4 && (
              <span className="gallery-card__interest-more">+{interests.length - 4}</span>
            )}
          </div>
        )}
      </div>

      {showActions && (
        <div className="gallery-card__actions">
          <button
            className="gallery-card__action-btn gallery-card__action-btn--primary"
            onClick={(e) => {
              e.stopPropagation();
              onSwipeStart?.();
            }}
          >
            View Profile →
          </button>
        </div>
      )}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders in large lists
export const GalleryCard = memo(GalleryCardComponent);
